import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { MultiSelectOption } from '../../../../../shared/models/select-option.model';
import { OptionTranslateService } from '../../../../../shared/services/option-translate.service';
import { Utils } from '../../../../../Utils/utils';
import { setValue, TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { CommonModule, DOCUMENT } from '@angular/common';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { customValidator } from '../../../../../Utils/validator';
import { HSCodeRequest } from '../../../../../shared/models/hs-code-request.model';
import { ApiEndPoints, ApiRoot } from '../../../../../shared/constants/api-endpoints';
import { ApiService } from '../../../../../core/services/api.service';
import { debounceTime, distinctUntilChanged, filter, finalize, lastValueFrom, Subject, switchMap, tap } from 'rxjs';
import * as XLSX from 'xlsx';
import { Router } from '@angular/router';
import { FieldValue, ServiceName } from '../../../../../shared/constants/services.enum';
import { SkeletonLoaderComponent } from "../../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { SharedService } from '../../../../../shared/services/shared.service';
import { DocumentName } from '../../../../../shared/constants/documents-name.enum';
import { RoutesPath } from '../../../../../shared/constants/routes-path';


interface Product {
  id: string;
  country: string;
  unit: string;
  quantity: string;
  productHSCode: string;
  productNameEn: string;
  productNameAr: string;
  unitPrice: string;
  category: string;
  weight: string;
  processingType?: string;
  processingCountry?: string;
}
@Component({
  selector: 'app-add-carnet-product',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, SkeletonLoaderComponent],
  templateUrl: './add-carnet-product.component.html',
  styleUrl: './add-carnet-product.component.scss'
})
export class AddCarnetProductComponent implements OnChanges, OnInit {
  hsCodeRequest: HSCodeRequest = new HSCodeRequest();

  @Input() isOpen = false;
  @Input() title = '';
  @Input() serviceName: any = ""
  @Input() viewProductList: boolean = false;
  @Input() product: any = "";
  @Input() allProducts: any[] = [];
  @Input() srSubType: any = "";
  @Input() MasterHsCodeList: MultiSelectOption[] = [];
  @Input() personalEffecType: string = '';
  @Input() checkDuplicateHSCode: boolean = false;
  @Input() addedProductsList: any = [];
  @Input() allowProductActions: boolean = true;
  @Input() mode: string = "add";


  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() onBtnClick = new EventEmitter<any>();
  fieldValue = FieldValue;
  serviceEnum = ServiceName;
  lookupKey = LookupKey;
  form!: FormGroup;
  file: File[] = [];
  hsCodeList: MultiSelectOption[] = [];
  loadingHsCode: boolean = false;
  rows: any[] = [];

  private search$ = new Subject<string>();
  loadingHsList: boolean = true;
  private readonly duplicateHsCountryMsg: string = "VALIDATION_MSG.PRODUCT_ALREADY_ADDED";
  lang: string = "en";
  
  constructor(
    private router:Router,
    private fb: FormBuilder,
    private api: ApiService,
    private sharedService: SharedService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.form = this.fb.group({
      id: "",
      mode: [null, Validators.required],
      search: [],
      country: [''],
      unit: [null],
      quantity: ['', [Validators.maxLength(12), customValidator('numberWithDecimal')]],
      bulkFile: [null],
      productHSCode: [null],
      productNameEn: [{ value: '', disabled: false }, [Validators.maxLength(200), customValidator('english')]],
      productNameAr: [{ value: '', disabled: false }, [Validators.maxLength(200), customValidator('arabic')]],
      unitPrice: ['', [customValidator('numberWithDecimal')]],
      category: [null],
      weight: ['', [Validators.maxLength(12), customValidator('numberWithDecimal')]],
      cooPersonalEffectType: [null],
      cooVehicleCategory: ['', [Validators.maxLength(50), customValidator('safeText')]],
      cooVehicleChassisNumber: ['', [Validators.maxLength(50), customValidator('safeText')]],
      cooVehicleColor: ['', [Validators.maxLength(50), customValidator('safeText')]],
      cooVehicleEngineNumber: ['', [Validators.maxLength(50), customValidator('safeText')]],
      cooVehicleMake: ['', [Validators.maxLength(100), customValidator('safeText')]],
      cooVehicleModel: ['', [Validators.maxLength(50), customValidator('safeText')]],
      cooVehicleSubMake: ['', [Validators.maxLength(50), customValidator('safeText')]],
      processingType: [null],
      processingCountry: [null],
    });
  }

  private scrollToProductList(): void {
    setTimeout(() => {
      const target = this.document?.getElementById('product-list');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
  // This method is used to manual
  get manual() { return this.form.get('mode')!.value === 'manual'; }
  // This method is used to bulk
  get bulk() { return this.form.get('mode')!.value === 'bulk'; }

  isGSPCertificate: boolean = false;
  isPersonalUse: boolean = false;

  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges): void {
    if (this.product) {
      this.editProduct(this.product);
    }
    if (this.srSubType == ServiceName.PERSONAL_EFFECTS_COO) {
      this.isPersonalUse = true;
      this.form.get('mode')?.setValue('manual');
      this.toggleRequired(this.manual && this.isPersonalUse ? true : false, [
        'cooPersonalEffectType'
      ]);
      if (this.personalEffecType) {
        this.form.get('cooPersonalEffectType')?.setValue(this.personalEffecType);
        this.form.get('cooPersonalEffectType')?.disable();
      }

    }
    if (this.srSubType == ServiceName.ISSUE_GSP_COO) {
      this.isGSPCertificate = true;
      this.form.get('mode')?.setValue('manual');
      this.form.get("country")?.setValue(FieldValue.UAE);
      this.form.get("country")?.disable();
    }
    if (this.srSubType != ServiceName.PERSONAL_EFFECTS_COO) {
      this.getHsCode();
    }
    this.applyValidation();
  }
  // This method is used to handle the on init lifecycle hook
  ngOnInit() {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: any) => {
          // ≤2 chars or cleared ⇒ just show preload
          // if (term.term.length <= 2) {
          //   this.members = this.preload;
          // }
        }),
        filter((term) => term.term.length > 2),
        tap(() => (this.loadingHsCode = true)),
        switchMap((term: any) =>
          this.getHSCodeBySearch(term.term)
            .pipe(finalize(() => (this.loadingHsCode = false)))
        )
      )
      .subscribe((response: any) => {
        if (response?.SiebelMessage && response?.SiebelMessage['PRODUCT']) {
          this.totalHsRecords = response.TotalRecords;
          let hsProducts = Utils.checkIsArray(response?.SiebelMessage['PRODUCT']);
          hsProducts.map((hs, index) => {
            let hsNewObj: MultiSelectOption = {
              id: index + 1,
              code: hs.ProductCode,
              name: hs.Description,
              description: hs.ProductNameARA,
            }
            this.hsCodeList = [...this.hsCodeList, hsNewObj]
          })
        }

      })
  }

  // This method is used to handle add product change event using value
  onAddProductChange(addMode: any) {
    if (addMode == "bulk") {
      this.getCountryAndMou('country');
      this.getCountryAndMou('category');
      this.getCountryAndMou('mou');
      this.getCountryAndMou('processingType');
    }
    this.applyValidation();
  }

  masterMouList: any = [];
  masterCountryList: any = [];
  masterCategoryList: any = [];
  masterProcessingTypeList: any = [];
  getCountryAndMou(type: string = "") {
    let lovTypeValue = LookupKey.COUNTRY;
    if (type == "mou") {
      lovTypeValue = LookupKey.UNIT_OF_MEASURE;
    } else if (type == "processingType" && this.serviceName != 'ATA_CARNET') {
      lovTypeValue = LookupKey.COO_DC_PROCESS_TYPE;
    } else if (type == "category") {
      lovTypeValue = LookupKey.ATA_PRODUCT_CATEGORY;
    } else {
      lovTypeValue = LookupKey.COUNTRY;
    }
    let request = {
      body: {
        ProcessName: "DC Get List Of Values WF",
        Type: lovTypeValue,
        "LanguageCode": this.sharedService.getCurrentLang() == "ar" ? 'ARA' : "ENU",
        ParentValue: ''
      }
    }
    this.api
      .post(ApiRoot.DCCICPIntegration, LookupKey.LOOKUP_URL, request)
      .subscribe({
        // This method is used to handle the next callback using result
        next: (result: any) => {
          if (result.SiebelMessage?.['List Of Values'] && result["Error Message"] == null || result["Error Message"] == "") {
            let listValue = Utils.checkIsArray(result.SiebelMessage['List Of Values']);
            if (type == "mou") {
              this.masterMouList = listValue;
            } else if (type == "processingType") {
              this.masterProcessingTypeList = listValue;
            } else if (type == "category") {
              this.masterCategoryList = listValue;
            } else {
              this.masterCountryList = listValue;
            }
          }
        }
      })
  }
  // This method is used to apply validation
  applyValidation() {
    if (this.serviceName == "ATA_CARNET") {
      this.toggleRequired(this.manual ? true : false, [
        'productNameEn', 'unit', 'quantity', 'unitPrice', 'country', 'category'
      ]);
    } else {
      this.form.get('productNameAr')?.clearValidators();
      this.form.get('productNameAr')?.setValidators([Validators.maxLength(200)]);
      this.form.get('productNameAr')?.updateValueAndValidity();

      this.toggleRequired(this.manual ? true : false, [
        'productHSCode', 'country'
      ]);

      this.toggleRequired(this.manual && this.isPersonalUse ? true : false, [
        'cooPersonalEffectType'
      ]);

      this.toggleRequired(this.form.get('cooPersonalEffectType')?.value == FieldValue.VEHICLES || this.form.get('cooPersonalEffectType')?.value == FieldValue.VEHICLES_AR ? true : false, [
        'cooVehicleCategory', 'cooVehicleChassisNumber', 'cooVehicleColor', 'cooVehicleEngineNumber', 'cooVehicleMake',
        'cooVehicleModel', 'cooVehicleSubMake'
      ]);
    }
  }

  // This method is used to toggle required using condition and controls
  private toggleRequired(condition: boolean, controls: string[]): void {
    Utils.addRemoveValidation(this.form, condition, controls);
  }

  // This method is used to get hs code for effect type using option
  getHsCodeForEffectType(option: any) {
    this.hsCodeList = [];
    this.totalHsRecords = 0;
    this.hsCodeRowNum = 0;
    if (this.product && option?.selectedLov.Value != this.product.cooPersonalEffectType) {
      this.form.get('productHSCode')?.setValue("");
    } else {
      if (!this.product) {
        this.form.get('productHSCode')?.setValue("");
      }
    }
    if (option?.selectedLov) {
      this.hsCodeRequest.body.IncomingHierarchy.ListOfDCHSCODELIST.PRODUCT.PersonalEffectType = option.selectedLov.Value;
      this.getHsCode();
    } else {
      this.hsCodeRequest.body.IncomingHierarchy.ListOfDCHSCODELIST.PRODUCT.PersonalEffectType = "";
    }
    this.applyValidation();
  }

  totalHsRecords: number = 0;
  hsCodeRowNum: number = 0;
  hsCodeSearchText: string = "";
  pageSize: any = 5;

  // This method is used to get hs code using load bulk hs code
  async getHsCode(loadBulkHsCode: boolean = false): Promise<MultiSelectOption[]> {

    this.loadingHsList = true;
    this.pageSize = loadBulkHsCode ? 5000 : 50;
    //this.MasterHsCodeList = [];

    this.loadingHsCode = true;

    const product = this.hsCodeRequest.body.IncomingHierarchy.ListOfDCHSCODELIST.PRODUCT;
    product.ProductCode = '';
    product.Description = this.hsCodeSearchText;
    do {
      this.hsCodeRequest.body.IncomingHierarchy.MessageId = Utils.generateUniqueId();
      this.hsCodeRequest.body.StartRowNum = this.hsCodeRowNum;
      this.hsCodeRequest.body.PageSize = this.pageSize;
      this.hsCodeRequest.body.NewQuery = this.hsCodeRowNum === 0 ? 'true' : 'false';
      this.hsCodeRequest.body.DCLanguage =this.lang == 'ar' ? 'ARA' : 'ENU';

      const response: any = await lastValueFrom(
        this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_HS_CODE, this.hsCodeRequest)
      );

      if (response?.SiebelMessage && response?.SiebelMessage['PRODUCT']) {
        this.totalHsRecords = response.TotalRecords;
        let hsProducts = Utils.checkIsArray(response?.SiebelMessage['PRODUCT']);

        const mappedProducts: MultiSelectOption[] = hsProducts.map(
          (hs, index) => ({
            id: this.hsCodeRowNum + index + 1,
            code: hs.ProductCode,
            name: hs.Description,
            description: hs.ProductNameARA,
          })
        );

        // if (loadBulkHsCode) {
        //   this.MasterHsCodeList = [...this.MasterHsCodeList, ...mappedProducts];
        // } else {
        //   this.hsCodeList = [...this.hsCodeList, ...mappedProducts];
        // }
        this.hsCodeList = mappedProducts; //[...this.hsCodeList, ...mappedProducts];
      } else {
        this.hsCodeList = [];
      }

      this.hsCodeRowNum += this.pageSize;
    } while (loadBulkHsCode && this.hsCodeRowNum < this.totalHsRecords);
    this.loadingHsCode = false;
    return this.hsCodeList;
  }

  // This method is used to get hs code by search using search text
  getHSCodeBySearch(searchText: string = "") {
    this.hsCodeRowNum = 0;
    this.hsCodeSearchText = searchText.trim();
    this.totalHsRecords = 0;
    this.hsCodeRequest.body.IncomingHierarchy.MessageId = Utils.generateUniqueId();
    this.hsCodeRequest.body.StartRowNum = this.hsCodeRowNum;
    this.hsCodeRequest.body.NewQuery = this.hsCodeRowNum == 0 ? "true" : "false";

    const product = this.hsCodeRequest.body.IncomingHierarchy.ListOfDCHSCODELIST.PRODUCT;
    // Reset both fields
    product.ProductCode = "";
    product.Description = "";

    // Check if search text is numeric only
    const isNumericSearch = /^\d+$/.test(this.hsCodeSearchText);
    if (isNumericSearch) {
      product.ProductCode = this.hsCodeSearchText;
    } else {
      product.Description = this.hsCodeSearchText.toUpperCase();
      product.ProductNameARA = "";
    }
    this.loadingHsCode = true;
    this.hsCodeList = [];

    return this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_HS_CODE,
      this.hsCodeRequest
    )
  }
  // This method is used to fetch next record using page
  fetchNextRecord(page: any) {
    this.hsCodeRowNum = Number(50) * (page - 1);
    this.getHsCode();
  }
  // This method is used to handle search using term
  handleSearch(searchText: string) {
    this.search$.next(searchText);
  }

  // This method is used to clear hs code
  clearHsCode() {
    this.hsCodeSearchText = "";
    this.hsCodeRowNum = 0;
    this.totalHsRecords = 0;
    if (this.selectedItems.length == 0) {
      this.form.get('productNameEn')?.enable({ emitEvent: false });
      this.form.get('productNameAr')?.enable({ emitEvent: false });
    }
    this.selectedItems = [];
    this.getHsCode();

  }

  selectedItems: any = []
  // This method is used to handle add item event using items
  onAddItem(items: any) {
    this.selectedItems = [];
    if (items.length > 0) {
      this.selectedItems = items;
      let firstIndex = this.selectedItems[this.selectedItems.length - 1]
      this.form.get('productHSCode')?.setValue(firstIndex.code);
      this.form.get('productHSCode')?.updateValueAndValidity();

      this.form.get('productNameEn')?.setValue(firstIndex.name);
      this.form.get('productNameAr')?.setValue(firstIndex.description);

      //Disable product name
      this.form.get('productNameEn')?.disable({ emitEvent: false });
      this.form.get('productNameEn')?.clearValidators();
      this.form.get('productNameEn')?.updateValueAndValidity({ emitEvent: false });

      this.form.get('productNameAr')?.disable({ emitEvent: false });
      this.form.get('productNameAr')?.clearValidators();
      this.form.get('productNameAr')?.updateValueAndValidity({ emitEvent: false });
    } else {
      this.form.get('productNameEn')?.enable();
      this.form.get('productNameAr')?.enable();
      this.form.get('productHSCode')?.setValue("");
      this.form.get('productHSCode')?.updateValueAndValidity();
    }
  }
  // This method is used to handle remove item event using items
  onRemoveItem(items: any) {
    if (items?.length < 1) {
      this.selectedItems = items;
      this.form.get('productHSCode')?.setValue('');
      this.form.get('productNameEn')?.enable({ emitEvent: false });
      this.form.get('productNameAr')?.enable({ emitEvent: false });
    }
  }

  // This method is used to edit product using product
  editProduct(product: any) {
    this.form.get("mode")?.setValue("manual");
    this.applyValidation();
    this.form.reset({ mode: "manual" });

    if (this.srSubType == ServiceName.ISSUE_GSP_COO) {
      this.form.get("country")?.setValue(FieldValue.UAE);
      this.form.get("country")?.disable();
    }

    let selectedProduct = {
      ...product,
      productHSCode: product?.ocrValidationErrors?.hsCode ? null : product?.productHSCode,
      country: product?.ocrValidationErrors?.country ? null : product?.country
    };
    Object.keys(selectedProduct).forEach(key => {
      const value = selectedProduct[key as keyof typeof selectedProduct];
      if (value !== null && value !== undefined) {
        this.form.get(key)?.setValue(value);
      }
    });
    if (!product.productHSCode) {
      this.form.get('productNameEn')?.enable();
      this.form.get('productNameAr')?.enable();
      this.form.get('productNameEn')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('productNameAr')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('productHSCode')?.updateValueAndValidity();
    } else {
      this.form.get('productNameEn')?.disable();
      this.form.get('productNameAr')?.disable();
      this.form.get('productNameEn')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('productNameAr')?.updateValueAndValidity({ emitEvent: false });
      this.form.get('productHSCode')?.updateValueAndValidity();
    }
    this.form.get("mode")?.setValue("manual");
    setTimeout(() => {
      const target = this.document?.getElementById('product-form');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.form.markAllAsTouched();
    }, 100);

    if(this.mode == 'view'){
       this.form.disable();
    }
  }

  // This method is used to upload using f
  upload(f: File) { this.file = [f]; this.form.get('bulkFile')!.setValue(f); }

  // This method is used to remove
  remove() { this.file = []; this.form.get('bulkFile')!.reset(); }

  // This method is used to save and add more
  saveAndAddMore(): void {
    this.form.markAllAsTouched();
    const invalidControls = Utils.getInvalidControls(this.form);
    if (this.form.valid) {
      const added = this.addProductToList();
      if (added) {
        this.form.get('productNameEn')?.enable();
        this.form.get('productNameAr')?.enable();
        this.scrollToProductList();
      }
    } else {
      this.form.markAllAsTouched();
    }
  }
  // This method is used to save close
  saveClose(): void {
    this.productDetailsErrorMsg = '';
    if (this.allProducts && this.allProducts.length > 0) {
      if (this.serviceName === 'ATA_CARNET' && this.hasIncompleteProducts()) {
        this.productDetailsErrorMsg = 'PRODUCT_FORM.MSG_MISSING_DETAILS';
        this.scrollToProductList();
        return;
      }
      const added = this.addProductToList();
      if (!added) {
        return;
      }
    } else {
      if (this.form.valid) {
        const added = this.addProductToList();
        if (!added) {
          return;
        }
      } else {
        this.form.markAllAsTouched();
        return;
      }
      if (this.serviceName === 'ATA_CARNET' && this.hasIncompleteProducts()) {
        this.productDetailsErrorMsg = 'PRODUCT_FORM.MSG_MISSING_DETAILS';
        this.scrollToProductList();
        return;
      }
    }

    let replaceExistingProduct: boolean = false;
    if (this.viewProductList && this.form.get("mode")?.value == "bulk") {
      replaceExistingProduct = false;
    } else {
      if (this.viewProductList && (this.form.get("mode")?.value == "manual" || this.form.get("mode")?.value == "")) {
        replaceExistingProduct = true;
      }
    }

    let productObj = {
      allProducts: this.allProducts, replaceAll: replaceExistingProduct
    }
    this.onBtnClick.emit(productObj);
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  // This method is used to total unit price
  get totalUnitPrice(): number {
    return this.allProducts.reduce((sum, row) => {
      const unitPrice = Number(row.unitPrice) || 0;
      const quantity = Number(row.quantity) || 0;
      return sum + unitPrice * quantity;
    }, 0);
  }

  // This method is used to add product to list
  addProductToList(): boolean {
    this.productDetailsErrorMsg = '';
    const formData = this.form?.getRawValue();
    const hasId = formData?.id !== undefined && formData?.id !== null && `${formData.id}` !== '';

    //06-Jan-2026 code commented for check duplicate HS Code as per :: Puneet

    // if (this.serviceName != 'ATA_CARNET' && this.checkDuplicateHSCode) {
    //   const mergedProducts = [...this.allProducts, ...this.addedProductsList];
    //   const duplicates = this.sharedService.findProductsWithSameHsAndCountry(
    //     mergedProducts,
    //     formData,
    //     formData?.id
    //   );
    //   if (duplicates.length > 0) {
    //     this.productDetailsErrorMsg = this.duplicateHsCountryMsg;
    //     this.scrollToProductList();
    //     return false;
    //   }
    // }

    if (hasId) {
      if (this.allProducts.length > 0) {
        this.allProducts = this.allProducts?.map(item =>
          item.id === formData.id ? { ...item, ...formData } : item
        );
      } else {
        const newItem = { ...formData };
        this.allProducts = [...this.allProducts, newItem];
      }
    } else {
      if (this.form.valid) {
        const newId = Utils.generateUniqueId();
        const newItem = { ...formData, id: newId };
        if (newItem.country && newItem.mode != "bulk") {
          this.allProducts = [...this.allProducts, newItem];
        } else {
          this.allProducts = [...this.allProducts];
        }
      } else {

      }
    }
    //Clear form after adding/updating
    if (this.srSubType == ServiceName.ISSUE_GSP_COO) {
      this.form.reset({ mode: "manual", "country": FieldValue.UAE });
    }
    else {
      if (this.form.get("mode")?.value== "" || this.form.get("mode")?.value == "manual") {
        this.form.reset({ mode: "manual" });
      } else {
        this.form.reset({ mode: this.form.get("mode")?.value == "bulk" ? 'bulk':'' });
      }
    }
    this.product = "";
    this.setEffectTypeValue();
    return true;
  }

  // This method is used to remove product using product
  removeProduct(product: any) {
    this.allProducts = this.allProducts.filter(item => item.id !== product.id);
    this.productDetailsErrorMsg = '';
    this.setEffectTypeValue();
  }

  setEffectTypeValue() {
    if (this.allProducts && this.allProducts.length > 0 && this.isPersonalUse) {
      if (!this.personalEffecType) {
        this.personalEffecType = this.allProducts[0].cooPersonalEffectType;
        this.form.get('cooPersonalEffectType')?.setValue(this.personalEffecType);
        this.form.get('cooPersonalEffectType')?.disable();
      } else {
        this.form.get('cooPersonalEffectType')?.setValue(this.personalEffecType);
        this.form.get('cooPersonalEffectType')?.disable();
      }
    }
  }

  isProductIncomplete(product: any): boolean {
    if (!product) {
      return true;
    }
    return this.requiredProductFields.some(field => this.isFieldMissing(product, field));
  }

  private isFieldMissing(product: any, field: string): boolean {
    const value = product?.[field];
    if (value === null || value === undefined) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    return false;
  }

  private hasIncompleteProducts(): boolean {
    if (!Array.isArray(this.allProducts)) {
      return false;
    }
    return this.allProducts.some(product => this.isProductIncomplete(product));
  }

  errorMsg: string = "";
  productDetailsErrorMsg: string = '';
  private readonly requiredProductFields: string[] = [
    'productNameEn',
    'quantity',
    'unit',
    'unitPrice',
    'category'
  ];
  // This method is used to download template file
  downloadTemplateFile(type: any) {
    if (type == "star-new") {
      return;
    }
    let filePath = "";
    if (this.srSubType == ServiceName.ISSUE_NEW_COO || this.srSubType == ServiceName.AMENDMENT_COO) {
      filePath = this.lang == 'ar' ? DocumentName.COO_PROD_TEMP_AR : DocumentName.COO_PROD_TEMP_ENG;
    } else {
      filePath = this.lang == 'ar' ? DocumentName.ATA_PROD_TEMP_ARA : DocumentName.ATA_PROD_TEMP_ENG;
    }
    const link = document.createElement('a');
    link.href = filePath;
    link.download; //= 'my-template.xlsx'; // suggested download name
    link.click();
  }

  // This method is used to contact us
  contactUs() {
    this.router.navigate([RoutesPath.CONTACT_US])
  }

  invalidProducts: any = [];
  // Helper: map missing flags object to list of labels for template iteration
  missingLabels(missing: any): string[] {
    if (!missing || typeof missing !== 'object') return [];
    const labels: string[] = [];
    if (missing.country) labels.push('COUNTRY OF ORIGIN');
    if (missing.unit) labels.push('UOM');
    if (missing.hsCode) labels.push('HS CODE');
    if (missing.unitPrice) labels.push('UNIT PRICE');
    if (missing.prodName) labels.push('PROD NAME');
    if (missing.prodNameAr) labels.push('PROD NAME ARA');
    if (missing.quantity) labels.push('QTY');
    if (missing.category) labels.push('CATEGORY');
    if (missing.productHSCodeOrName) labels.push('HS CODE or PROD NAME');
    if (missing.processingType) labels.push('PROCESSING TYPE');
    if (missing.processingCCountry) labels.push('PROCESSING COUNTRY');

    return labels;
  }

  private normalizeValue(val: any): string {
    return (val ?? '').toString().trim();//.toUpperCase();
  }

  private isValueInMaster(list: any[], value: string, key: string = 'Value'): boolean {
    const normalized = this.normalizeValue(value);
    if (!normalized) return true; // nothing to validate
    if (!Array.isArray(list) || list.length === 0) return true; // skip if master unavailable
    return list.some((lov) => this.normalizeValue(lov?.[key]) === normalized);
  }

  private getInvalidLookupFields(product: Product): string[] {
    const invalid: string[] = [];
    if (product?.country) {
      const isCountryExist = this.isValueInMaster(this.masterCountryList, product.country);
      if (!isCountryExist) {
        invalid.push(`COUNTRY OF ORIGIN: ${product.country}`);
      }
    }
    if (product?.unit) {
      if (!this.isValueInMaster(this.masterMouList, product.unit)) {
        invalid.push(`UOM: ${product.unit}`);
      }
    }
    if (product?.category) {
      if (!this.isValueInMaster(this.masterCategoryList, product.category, 'Description')) {
        invalid.push(`CATEGORY: ${product.category}`);
      }
    }
    if (product?.processingType) {
      if (!this.isValueInMaster(this.masterProcessingTypeList, product.processingType)) {
        invalid.push(`PROCESSING TYPE: ${product.processingType}`);
      }
    }
    if (product?.processingCountry) {
      if (!this.isValueInMaster(this.masterCountryList, product.processingCountry)) {
        invalid.push(`PROCESSING COUNTRY: ${product.processingCountry}`);
      }
    }
    return invalid;
  }
  // This method is used to handle file change event using evt
  onFileChange(evt: any) {
    this.errorMsg = '';
    this.invalidProducts = [];
    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.product = [];
    this.allProducts = [];
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<any[]>(sheet, {
          header: 1,
          defval: ''
        }) as any[][];

        if (!rows.length) {
          this.errorMsg = 'The Excel sheet is empty.';
          return;
        }

        // This method is used to normalize using heading
        const normalize = (h: any) =>
          String(h ?? '').trim().toUpperCase().replace(/\s+/g, ' ');

        const headerRow = (rows[0] as any[]).map(normalize);
        // This method is used to idx using name
        const idx = (name: string) => headerRow.indexOf(normalize(name));
        const col = {
          country: idx('COUNTRY OF ORIGIN'),
          unitPrice: idx('UNIT PRICE'),
          qty: idx('QTY'),
          uom: idx('UOM'),
          prodName: idx('PROD NAME'),
          prodNameAr: idx('PROD NAME ARA'),
          hsCode: idx('HS CODE'),
          weight: idx('WEIGHT'),
          category: idx('CATEGORY'),
          processingType: idx('PROCESSING TYPE'),
          processingCountry: idx('PROCESSING COUNTRY')
        };

        // Validate required headers exist
        let requiredHeaders: string[] = [];

        if (this.serviceName === 'COO') {
          // COO requires only HS CODE and COUNTRY OF ORIGIN
          requiredHeaders = [
            'HS CODE',
            'COUNTRY OF ORIGIN',
            'PROCESSING TYPE',
            'PROCESSING COUNTRY'
          ];
        }
        else if (this.serviceName === 'ATA_CARNET') {
          // ATA requires these fields always
          const ataMandatory = ['UOM', 'QTY', 'UNIT PRICE', 'COUNTRY OF ORIGIN'];

          // Check if at least HS CODE or PROD NAME exists
          const hasHSCode = headerRow.includes('HS CODE');
          const hasProdName = headerRow.includes('PROD NAME');

          if (!hasHSCode && !hasProdName) {
            this.errorMsg = `Your file must contain either "HS CODE" or "PROD NAME" for ATA Carnet.`;
            return;
          }
        }

        const missingHeaders = requiredHeaders.filter(h => idx(h) === -1);
        if (missingHeaders.length) {
          this.errorMsg = `Missing required columns: ${missingHeaders.join(', ')}. Please use the provided template headers exactly.`;
          return;
        }

        const newProducts: Product[] = [];
        const hsCountryKeys = new Set<string>();
        if (this.serviceName === 'ATA_CARNET') {
          (this.allProducts || []).forEach(existing => {
            const key = this.buildHsCountryKey(existing);
            if (key) {
              hsCountryKeys.add(key);
            }
          });
        }

        for (let r = 1; r < rows.length; r++) {
          const row = rows[r] as any[];
          if (!row || row.every(cell => String(cell).trim() === '')) continue;

          const product: Product = {
            id: Utils.generateUniqueId(),
            country: String(row[col.country] ?? '').trim(),
            unit: String(row[col.uom] ?? '').trim(),
            quantity: String(row[col.qty] ?? '').trim(),
            productHSCode: String(row[col.hsCode] ?? '').trim(),
            productNameEn: String(row[col.prodName] ?? '').trim(),
            productNameAr: String(row[col.prodNameAr] ?? '').trim(),
            unitPrice: String(row[col.unitPrice] ?? '').trim(),
            category: String(row[col.category] ?? '').trim(),
            weight: String(row[col.weight] ?? '').trim(),
            processingType: String(row[col.processingType] ?? '').trim(),
            processingCountry: String(row[col.processingCountry] ?? '').trim()
          };

          const hasHsCode = !!product.productHSCode;
          const masterHs: any = hasHsCode
            ? this.MasterHsCodeList?.find((h) => h.code === product.productHSCode)
            : null;

          // Rule 1: If service is NOT ATA_CARNET, HS code is mandatory
          if (this.serviceName !== "ATA_CARNET" && !hasHsCode) {
            continue;
          }

          // Rule 2: If HS code provided, must match master list
          if (hasHsCode && !masterHs) {
            this.invalidProducts.push({
              row: r + 1,
              hsCode: product.productHSCode,
              type: 'invalidHSCode',
              missing: {
                country: !product.country,
                unit: !product.unit,
                unitPrice: !product.unitPrice
              }
            });
            continue;
          }
          // Rule 3: Check mandatory fields
          if (this.serviceName === 'COO') {
            // COO: only country + hsCode required
            if (!product.country || !product.productHSCode) {
              this.invalidProducts.push({
                row: r + 1,
                hsCode: product.productHSCode,
                type: 'missingField',
                missing: {
                  country: !product.country,
                  productHSCode: !product.productHSCode
                }
              });
              continue;
            }
          }

          // ATA Carnet
          else if (this.serviceName === 'ATA_CARNET') {
            // Must have HS Code OR Product Name
            const hasCodeOrName = product.productHSCode || product.productNameEn;

            if (
              !hasCodeOrName ||
              !product.country ||
              !product.unit ||
              !product.unitPrice ||
              !product.quantity ||
              !product.category
            ) {
              this.invalidProducts.push({
                row: r + 1,
                hsCode: product.productHSCode,
                type: 'missingField',
                missing: {
                  productHSCodeOrName: !hasCodeOrName,
                  country: !product.country,
                  unit: !product.unit,
                  unitPrice: !product.unitPrice,
                  quantity: !product.quantity,
                  category: !product.category
                }
              });
              continue;
            }
          }

          const invalidLookupFields = this.getInvalidLookupFields(product);
          if (invalidLookupFields.length) {
            this.invalidProducts.push({
              row: r + 1,
              hsCode: product.productHSCode,
              type: 'invalidLookupValue',
              invalidFields: invalidLookupFields
            });
            continue;
          }

          const duplicateKey = this.buildHsCountryKey(product);
          //06-Jan-2026 code commented for check duplicate HS Code as per :: Puneet
          
          // if (this.serviceName != 'ATA_CARNET') {
          //   const mergedProducts = [...this.allProducts, ...this.addedProductsList];
          //   const existingDupes = this.sharedService.findProductsWithSameHsAndCountry(
          //     mergedProducts,
          //     product
          //   );
          //   if (existingDupes.length > 0) {
          //     this.invalidProducts.push({
          //       row: r + 1,
          //       hsCode: product.productHSCode,
          //       country: product.country,
          //       type: 'duplicateCombination',
          //       message: this.duplicateHsCountryMsg
          //     });
          //     continue;
          //   }
          //   if (duplicateKey && hsCountryKeys.has(duplicateKey)) {
          //     this.invalidProducts.push({
          //       row: r + 1,
          //       hsCode: product.productHSCode,
          //       country: product.country,
          //       type: 'duplicateCombination',
          //       message: this.duplicateHsCountryMsg
          //     });
          //     continue;
          //   }
          //   if (duplicateKey) {
          //     hsCountryKeys.add(duplicateKey);
          //   }
          // }

          // Rule 4: If HS code valid update product names from master
          if (hasHsCode && masterHs) {
            product.productNameEn = masterHs.name;
            product.productNameAr = masterHs.description;
          }
          newProducts.push(product);
        }


        this.allProducts = this.allProducts.concat(newProducts);
        input.value = '';
        this.viewProductList = true;

      } catch (e: any) {
        this.errorMsg = 'Could not read the Excel file. ' + (e?.message ?? '');
        input.value = '';
      }
    };

    // This method is used to onerror
    reader.onerror = () => {
      this.errorMsg = 'Failed to load the file.';
      input.value = '';
    };

    reader.readAsArrayBuffer(file);
  }

  private buildHsCountryKey(product: any): string | null {
    const hsCode = (product?.productHSCode ?? '').toString().trim().toUpperCase();
    const country = (product?.country ?? '').toString().trim().toUpperCase();
    if (!hsCode || !country) {
      return null;
    }
    return `${hsCode}__${country}`;
  }

  close(){
     this.isOpenChange.emit(false);
  }

}
